import { useEffect, useRef, useState } from "react";
import { Trans } from "react-i18next";
import { useNavigate } from "react-router";
import { CreditCard, LogoFull } from "assets/svg";
import routes from "routes/routes";
import { useAppSelector } from "store/hooks";
import { userMetadataSelector } from "store/selectors";

import { PayDistributionType } from "types/BETypes";

import { IExternalWidgetProps } from "./components/BankAccountsForm/types";
import { FormType } from "./components/BankAccountsForm/validationSchema";
import { EDistributionOptions, IProps, PayDistributionWidgetType } from "./types";
import useBankAccountsInitialValues from "./useBankAccountsInitialValues";
import { useCommonModals } from "./useCommonModals";
import {
  CACButtonsContainer,
  CACContentContainer,
  CACCreateAccountButton,
  CACLogoContainer,
  CACText,
  CACTextLink,
  CACTitle,
  CompanyAccountCardContainer,
  MethodSelectionItem,
  MSIActiveLabel,
  MSIIcon,
  MSIText,
} from "./styles";

const usePayDistribution = (props: IProps) => {
  //NOTE:::Constants and external hooks
  const {
    initialStep,
    disabledSteps,
    widgetType,
    onNoAccountsModalCancel,
    onNoAccountsModalConfirm,
  } = props;

  const currentUser = useAppSelector(userMetadataSelector);
  const firstPayDistributionMethod = currentUser?.activeCompanyUser?.defaultPayDistribution?.[0];
  const navigate = useNavigate();
  const { showNoAccountsModal, showNavigationWarningModal } = useCommonModals();
  const distributionOptions = [EDistributionOptions.BANK_ACCOUNT, EDistributionOptions.CHECK];
  const translationPrefix = `components.pay_distribution`;

  //NOTE:::States
  const [currentStep, setCurrentStep] = useState<EDistributionOptions | undefined>(undefined);
  const [currentFormData, setCurrentFormData] = useState<FormType | undefined>(undefined);
  const [isInEditMode, setIsInEditMode] = useState<boolean>(false);
  const bankAccountFormRef = useRef<IExternalWidgetProps | null>(null);

  const redirectToBankAccounts = () => {
    navigate(routes.EMPLOYEE_BANK_ACCOUNTS);
  };

  const handleNoBankAccountsModalClose = () => {
    onNoAccountsModalCancel ? onNoAccountsModalCancel() : switchStep(EDistributionOptions.CHECK);
  };
  const handleNoBankAccountsModalConfirm = () => {
    onNoAccountsModalConfirm ? onNoAccountsModalConfirm() : redirectToBankAccounts();
  };

  const { isLoading, initialFormData, combinedAccounts, getInitialFormData, companyAccount } =
    useBankAccountsInitialValues({
      widgetType,
      onNoAccountsModalCancel: handleNoBankAccountsModalClose,
      onNoAccountsModalConfirm: handleNoBankAccountsModalConfirm,
    });

  useEffect(() => {
    if (initialFormData && combinedAccounts && !currentStep) {
      initializeStep();
    }
  }, [initialFormData, combinedAccounts]);

  //NOTE:::Methods
  const initializeStep = () => {
    if (initialStep) {
      setCurrentStep(initialStep);
    } else if (
      firstPayDistributionMethod?.type === PayDistributionType.CHECK ||
      (widgetType === PayDistributionWidgetType.ONBOARDING && !combinedAccounts?.length)
    ) {
      setCurrentStep(EDistributionOptions.CHECK);
    } else {
      setCurrentStep(EDistributionOptions.BANK_ACCOUNT);
    }
  };

  const handleBankAccountFormConfirmPayDistribution = (data: FormType) => {
    if (isInEditMode) {
      setIsInEditMode(false);
    }
    setCurrentFormData(data);
  };

  const shouldShowRedirectModal = () => {
    return !!bankAccountFormRef.current?.isDataUnsaved();
  };

  const switchStep = (item: EDistributionOptions) => {
    if (disabledSteps?.includes(item)) return;
    if (item === EDistributionOptions.CHECK) {
      if (shouldShowRedirectModal()) {
        showNavigationWarningModal(() => {
          if (isInEditMode) {
            handleCancelInitEditMode();
          }
          setCurrentStep(item);
        });
      } else {
        setCurrentStep(item);
      }
    } else if (!combinedAccounts?.length) {
      showNoAccountsModal(handleNoBankAccountsModalConfirm, handleNoBankAccountsModalClose);
    } else {
      if (item === EDistributionOptions.BANK_ACCOUNT) {
        const formData = getInitialFormData();
        if (formData) {
          setCurrentFormData(formData);
        }
      }

      setCurrentStep(item);
    }
  };

  const handleSetCompanyBankAccountAsDefault = () => {
    setCurrentStep(EDistributionOptions.BANK_ACCOUNT);
    setIsInEditMode(true);
    bankAccountFormRef.current?.isDataUnsaved?.();
  };

  const handleCancelInitEditMode = () => {
    setIsInEditMode(false);
  };

  //NOTE:::Render methods
  const renderMethodSelectionItem = (item: EDistributionOptions) => {
    const isDisabledBankAccountOptionsWithNoData =
      item === EDistributionOptions.BANK_ACCOUNT &&
      widgetType === PayDistributionWidgetType.ONBOARDING &&
      !combinedAccounts?.length;
    const isDisabled = disabledSteps?.includes(item) || isDisabledBankAccountOptionsWithNoData;
    const className = `${currentStep === item ? "active" : ""} ${isDisabled ? "disabled" : ""}`;
    return (
      <MethodSelectionItem
        className={className}
        onClick={() => {
          if (isDisabled) return;
          switchStep(item);
        }}
        data-testid={`pay-method-selection-item_${item}`}
      >
        <MSIIcon>
          <CreditCard />
        </MSIIcon>
        <MSIText>
          <Trans i18nKey={`${translationPrefix}.pay_distribution_options.${item}`} />
        </MSIText>
        {currentStep === item && (
          <MSIActiveLabel>
            <Trans i18nKey={`${translationPrefix}.pay_distribution_option_selected`} />
          </MSIActiveLabel>
        )}
      </MethodSelectionItem>
    );
  };

  const renderCompanyCard = () => {
    //NOTE::: Not displayed on the page right now, mb will be used later
    return (
      <CompanyAccountCardContainer>
        <CACLogoContainer>
          <LogoFull />
        </CACLogoContainer>
        <CACContentContainer>
          <CACTitle>
            <Trans i18nKey={`${translationPrefix}.company_account_card.title`} />
          </CACTitle>
          <CACText>
            <Trans
              i18nKey={`${translationPrefix}.company_account_card.text`}
              components={{ 1: <CACTextLink to={routes.BANKING} /> }}
            />
          </CACText>
        </CACContentContainer>
        <CACButtonsContainer>
          {/* TODO::: add modal */}
          <CACCreateAccountButton to={""}>
            <Trans i18nKey={`${translationPrefix}.company_account_card.create_account_button`} />
          </CACCreateAccountButton>
        </CACButtonsContainer>
      </CompanyAccountCardContainer>
    );
  };

  return {
    currentStep,
    distributionOptions,
    bankAccountFormRef,
    isInEditMode,
    isLoading,
    initialFormData: currentFormData || initialFormData,
    combinedAccounts,
    redirectToBankAccounts,
    renderMethodSelectionItem,
    renderCompanyCard,
    shouldShowRedirectModal,
    handleNoBankAccountsModalConfirm,
    handleNoBankAccountsModalClose,
    handleBankAccountFormConfirmPayDistribution,
    handleSetCompanyBankAccountAsDefault,
    handleCancelInitEditMode,
  };
};

export default usePayDistribution;
